import React, { useState, useEffect } from 'react';
import EditableSection from 'components/v2/EditableSection';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import Select from 'components/v2/select';
import { useTranslation } from 'react-i18next';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { checkPermissonForEditShipperBillingModule } from './utils';

const monthOptions = [
  {
    value: '1',
    name: 'Jan',
    localizationSlug: 'JAN',
  },
  {
    value: '2',
    name: 'Feb',
    localizationSlug: 'FEB',
  },
  {
    value: '3',
    name: 'Mar',
    localizationSlug: 'MAR',
  },
  {
    value: '4',
    name: 'Apr',
    localizationSlug: 'APR',
  },
  {
    value: '5',
    name: 'May',
    localizationSlug: 'MAY',
  },
  {
    value: '6',
    name: 'Jun',
    localizationSlug: 'JUN',
  },
  {
    value: '7',
    name: 'Jul',
    localizationSlug: 'JUL',
  },
  {
    value: '8',
    name: 'Aug',
    localizationSlug: 'AUG',
  },
  {
    value: '9',
    name: 'Sep',
    localizationSlug: 'SEP',
  },
  {
    value: '10',
    name: 'Oct',
    localizationSlug: 'OCT',
  },
  {
    value: '11',
    name: 'Nov',
    localizationSlug: 'NOV',
  },
  {
    value: '12',
    name: 'Dec',
    localizationSlug: 'DEC',
  },
];

export const CreditCardSummary = ({ fields }) => {
  const { t } = useTranslation();
  const formatCardNumber = (data = {}) => {
    const { last4, brand } = data;
    if (last4 && brand) {
      const currentCard = t('BRAND_ENDING_IN', {
        brand,
        last4,
      });
      return currentCard;
    }
    return '';
  };

  const formatCardExpiry = (data = {}) => {
    const { expiry_month, expiry_year } = data;
    if (expiry_month && expiry_year) {
      const cardExpiryDate = t('EXPIRES_ON', {
        expiry_month: expiry_month.toString().padStart(2, '0'),
        expiry_year,
      });
      return cardExpiryDate;
    }
    return '';
  };

  const cardNumber = formatCardNumber(fields);
  const cardExpiryDate = formatCardExpiry(fields);

  return (
    <div className="grid grid-cols-2 gap-4">
      <DescriptionListItem label={t('STRIPE_ID')}>
        {fields.stripeId ? fields.stripeId : t('NOT_SET')}
      </DescriptionListItem>
      <DescriptionListItem label={t('CARD')}>
        {cardNumber || t('NO_CC_REGISTERED')} <br />
        {cardExpiryDate || ''}
      </DescriptionListItem>
    </div>
  );
};

export const CreditCardForm = ({
  fields,
  onChange,
  saving,
  onSaveShipper,
  onSaveSuccess,
  onSaveFail,
}) => {
  const { t } = useTranslation();
  const requiredFields = ['number', 'expiry_month', 'expiry_year', 'cvv'];

  const validate = () => {
    const error = {};
    requiredFields.forEach((key) => {
      if (!fields[key]) {
        error[key] = t('FIELD_IS_REQUIRED', { field: t(key.toUpperCase()) });
      }
    });
    onChange('error', error);
    if (Object.keys(error).length) {
      return false;
    }
    return true;
  };

  const saveForm = async () => {
    try {
      if (validate()) {
        await onSaveShipper();
        onSaveSuccess(fields);
      }
    } catch (e) {
      onSaveFail();
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <div className="col-span-full">
        <Label>{t('NUMBER')}</Label>
        <TextField
          className="mt-1"
          placeholder={t('CREDIT_CARD_NUMBER')}
          value={fields.number}
          onChange={(val) => {
            onChange('number', val);
          }}
        />
        {!fields.number && fields?.error?.number && (
          <ErrorLabel className="mt-1">{fields?.error?.number}</ErrorLabel>
        )}
      </div>
      <div className="">
        <Label>{t('EXPIRY_MONTH')}</Label>
        <Select
          labelClassName="m-0"
          items={monthOptions}
          textKey="localizationSlug"
          valueKey="value"
          selected={fields?.expiry_month}
          onSelect={(option) => onChange('expiry_month', option?.value)}
        />
        {!fields.expiry_month && fields?.error?.expiry_month && (
          <ErrorLabel className="mt-1">
            {fields?.error?.expiry_month}
          </ErrorLabel>
        )}
      </div>
      <div className="">
        <Label>{t('EXPIRY_YEAR')}</Label>
        <TextField
          className="mt-1"
          placeholder={t('EXPIRY_YEAR')}
          value={fields.expiry_year}
          onChange={(val) => onChange('expiry_year', val)}
        />
        {!fields.expiry_year && fields?.error?.expiry_year && (
          <ErrorLabel className="mt-1">{fields?.error?.expiry_year}</ErrorLabel>
        )}
      </div>
      <div className="">
        <Label>{t('CVV')}</Label>
        <TextField
          className="mt-1"
          placeholder={t('CVV')}
          value={fields.cvv}
          onChange={(val) => onChange('cvv', val)}
        />
        {!fields.cvv && fields?.error?.cvv && (
          <ErrorLabel className="mt-1">{fields?.error?.cvv}</ErrorLabel>
        )}
      </div>
    </div>
  );
};

const CreditCard = ({ data, className, onUpdateShipper }) => {
  const { t } = useTranslation();
  const [initialData, setInitialData] = useState(data);
  const [creditCardData, setCreditCardData] = useState(data);

  const getInitData = () => {
    if (data) {
      setInitialData(data);
      setCreditCardData(data);
    }
  };
  const handleSave = async () => {
    const updates = { ...creditCardData };
    if (updates.expiry_month) {
      updates.expiry_month = parseInt(updates.expiry_month, 10);
    }
    await onUpdateShipper(updates);
  };

  const onChange = (field, value) => {
    setCreditCardData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setInitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };

  useEffect(() => {
    getInitData();
  }, [data]);

  return (
    <EditableSection
      title={t('CREDIT_CARD')}
      canEdit={checkPermissonForEditShipperBillingModule()}
      className={className}
      onSave={handleSave}
    >
      {(editMode, saving, onSaveSuccess, onSaveFail) =>
        editMode ? (
          <CreditCardForm
            fields={creditCardData}
            onChange={onChange}
            saving={saving}
            onSaveSuccess={handleAfterSave(onSaveSuccess)}
            onSaveFail={() => onSaveFail()}
            onSaveShipper={() => handleSave()}
          />
        ) : (
          <CreditCardSummary fields={initialData} />
        )
      }
    </EditableSection>
  );
};

export default CreditCard;
